import { Availability, AvailabilityType } from '@luminovo/api-contracts';
import { assertUnreachable } from '@luminovo/commons';
import { ComparableResult } from '@luminovo/commons';

function compareLeadTimeDays(daysLeft: number, daysRight: number): ComparableResult {
    if (daysLeft < daysRight) {
        return ComparableResult.less;
    } else if (daysLeft > daysRight) {
        return ComparableResult.greater;
    } else {
        return ComparableResult.equal;
    }
}

/*
 * Utility function to only compare the price break, on order, and lead time types of availability,
 * under the assumption that the lead time days are equal.
 */
function availabilityTypeComparator(
    typeLeft: AvailabilityType.OnOrder | AvailabilityType.LeadTime,
    typeRight: AvailabilityType.OnOrder | AvailabilityType.LeadTime,
): ComparableResult {
    switch (typeLeft) {
        case AvailabilityType.OnOrder:
            switch (typeRight) {
                case AvailabilityType.OnOrder:
                    return ComparableResult.equal;
                case AvailabilityType.LeadTime:
                    return ComparableResult.less;
                default:
                    return assertUnreachable(typeRight);
            }
        case AvailabilityType.LeadTime:
            switch (typeRight) {
                case AvailabilityType.OnOrder:
                    return ComparableResult.greater;
                case AvailabilityType.LeadTime:
                    return ComparableResult.equal;
                default:
                    return assertUnreachable(typeRight);
            }
    }
}

/*
 * Utility function to only compare non-null availabilities.
 */
function concreteLeadTimeComparator(availabilityLeft: Availability, availabilityRight: Availability): ComparableResult {
    /* oxlint-disable no-fallthrough */
    switch (availabilityLeft.type) {
        case AvailabilityType.Stock:
            switch (availabilityRight.type) {
                case AvailabilityType.Stock:
                    return ComparableResult.equal;
                case AvailabilityType.OnOrder:
                case AvailabilityType.OnOrderWithUnknownLeadTime:
                case AvailabilityType.LeadTime:
                    return ComparableResult.less;
            }
        case AvailabilityType.OnOrder:
        case AvailabilityType.LeadTime:
            switch (availabilityRight.type) {
                case AvailabilityType.Stock:
                    return ComparableResult.greater;
                case AvailabilityType.OnOrder:
                case AvailabilityType.LeadTime:
                    const dayComparison = compareLeadTimeDays(availabilityLeft.days, availabilityRight.days);
                    if (dayComparison === ComparableResult.equal) {
                        // special case: if the lead time days are equal,
                        // then we sort price break availability before on order availability,
                        // and on order availability before lead time availability
                        return availabilityTypeComparator(availabilityLeft.type, availabilityRight.type);
                    } else {
                        return dayComparison;
                    }
                case AvailabilityType.OnOrderWithUnknownLeadTime:
                    return ComparableResult.less;
            }
        case AvailabilityType.OnOrderWithUnknownLeadTime:
            switch (availabilityRight.type) {
                case AvailabilityType.Stock:
                case AvailabilityType.OnOrder:
                case AvailabilityType.LeadTime:
                    return ComparableResult.greater;
                case AvailabilityType.OnOrderWithUnknownLeadTime:
                    return ComparableResult.equal;
            }
    }
}

/**
 * Returns a comparator to sort lead times by.
 * The comparison is not just only done on the lead time in days,
 * since the order also depends on the type of the availability.
 */
export function compareAvailability(
    availabilityLeft: null | undefined | Availability,
    availabilityRight: null | undefined | Availability,
): ComparableResult {
    // first handle null and undefined case
    if (!availabilityLeft) {
        if (!availabilityRight) {
            return ComparableResult.equal;
        } else {
            return ComparableResult.greater;
        }
    }
    if (!availabilityRight) {
        return ComparableResult.less;
    }
    return concreteLeadTimeComparator(availabilityLeft, availabilityRight);
}
